import React, { useState } from 'react';
import { Box, Button, Flex, Text, ProgressBar } from '@indeed/ifl-components';
import { ChevronLeft, Close } from '@indeed/ifl-icons';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { usePageFlow } from '../../lib/pageFlow/usePageFlow';
import { PremiumBadge } from '../../components/PremiumBadge/PremiumBadge';
import ExitDialog from '../../components/ExitDialog/ExitDialog';
import useGettext from '../../util/GettextFactory';
import { jsProThemeButton, jsProThemeProgressBar } from './components/jsProTheme/jsProTheme';
import { useInitialState } from '../../lib/context/InitialStateContext';
import { useIsProBrandingEnabled } from '../../lib/hooks/useIsProBrandingEnabled';
import { useApplyForMeCopy } from './hooks/useApplyForMeCopy';

/**
 * iOS-style navigation for Auto Apply Setup flow
 * Features:
 * - Back button with arrow icon
 * - Centered "Set up Auto Apply" title
 * - Close button with X icon
 * - Progress bar showing completion percentage
 *
 * Progressive-automation variant (proctor `rnhp_auto_apply_banner_tst` value 2):
 * a lightweight nav bar that shows only the back and close buttons across every
 * step of the flow — no centered title and no progress bar (IAFE-2330).
 */
const AutoApplySetupNavigation: React.FC = () => {
    const { goBack, canGoBack, allSteps, currentStep } = usePageFlow();
    const gt = useGettext();
    const { isJobSearchApp, returnUrl } = useInitialState();
    const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
    const isProBrandingEnabled = useIsProBrandingEnabled();
    const copy = useApplyForMeCopy();

    // Progressive-automation entry: when the user is in the value-2 bucket the
    // whole setup flow uses a lightweight nav bar (back + close only).
    const { getGroup } = useProctorGroups();
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;

    const handleBack = () => {
        // goBack exits the sub-flow at its first step and steps back otherwise.
        void goBack();
    };

    // Calculate current step information
    const getCurrentStepInfo = () => {
        if (!allSteps || !currentStep) {
            return { currentStepIndex: 0, totalSteps: 3, visibleSteps: [] };
        }

        // Filter to only the visible navigation steps (exclude complete page)
        const visibleSteps = allSteps.filter(
            (step) => !step.routes.some((route) => route.includes('/complete')),
        );

        const currentStepIndex = visibleSteps.findIndex((step) =>
            step.routes.includes(currentStep),
        );

        return {
            currentStepIndex: currentStepIndex !== -1 ? currentStepIndex : 0,
            totalSteps: visibleSteps.length,
            visibleSteps,
        };
    };

    const { currentStepIndex, visibleSteps } = getCurrentStepInfo();

    // Check if we're on the complete page
    const isCompletePage = currentStep === '/auto-apply-setup/complete';

    // In the Job Search app the back/close buttons are suppressed (the app
    // provides its own chrome). In the progressive-automation variant there is
    // also no title or progress bar, so the nav would render as an empty bar and
    // leave a blank section at the top. Render nothing in that case.
    if (isJobSearchApp && isProgressiveAutomation && !isCompletePage) {
        return null;
    }

    // Show the Back button whenever the flow can go back; hidden on the complete
    // page. canGoBack is always true inside a sub-flow detour, so every detour
    // step shows it too. The first main-spine step (canGoBack false) shows none.
    const showBackButton = !isCompletePage && canGoBack;

    const handleClose = () => {
        setIsExitDialogOpen(true);
    };

    const handleExitClick = () => {
        window.location.href = returnUrl;
    };

    const handleCancelClick = () => {
        setIsExitDialogOpen(false);
    };

    // Calculate progress percentage based on current step
    const calculateProgress = () => {
        if (visibleSteps.length === 0) return 0;

        const progress = ((currentStepIndex + 1) / visibleSteps.length) * 100;
        return Math.min(progress, 100); // Cap at 100%
    };

    return (
        <Flex sx={{ gap: 4, flexDirection: 'column', paddingInline: 4 }}>
            {/* Header: Back + Title + Close */}
            <Flex
                sx={{
                    position: 'relative',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {/* Left side: Back button OR spacer on complete page */}
                {!showBackButton ? (
                    <Box sx={{ inlineSize: '60px' }} />
                ) : (
                    <Button
                        variant="tertiary"
                        onClick={handleBack}
                        aria-label={gt.gettext('Back')}
                        sx={{
                            ...(isProBrandingEnabled ? jsProThemeButton.tertiary : {}),
                            // Use visibility to keep the layout consistent in Job Search app
                            visibility: isJobSearchApp ? 'hidden' : undefined,
                        }}
                    >
                        <ChevronLeft size="md" aria-hidden />
                    </Button>
                )}

                {/* Center: Indeed Pro on complete page, title on other pages */}
                {isCompletePage ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        {isProBrandingEnabled ? (
                            <>
                                <PremiumBadge size="md" aria-hidden />
                                <Text
                                    level={3}
                                    variant="contrast"
                                    sx={{
                                        fontWeight: 700,
                                        marginInlineStart: 1,
                                    }}
                                >
                                    Indeed Pro
                                </Text>
                            </>
                        ) : (
                            <>
                                <Text
                                    level={3}
                                    variant="contrast"
                                    sx={{
                                        fontWeight: 700,
                                        marginInlineStart: 1,
                                    }}
                                >
                                    {copy.productName}
                                </Text>
                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'foreground.strong',
                                        borderRadius: 'sm',
                                        paddingInline: '6px',
                                        paddingBlock: '4px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginInlineStart: 1,
                                        verticalAlign: 'text-bottom',
                                    }}
                                >
                                    <Text
                                        as="span"
                                        level={1}
                                        sx={{
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            color: 'foreground.strong',
                                        }}
                                    >
                                        {gt.gettext('Beta')}
                                    </Text>
                                </Box>
                            </>
                        )}
                    </Box>
                ) : isProgressiveAutomation ? (
                    // Lightweight variant: no title, back/close pushed to the edges
                    <Box sx={{ inlineSize: '60px' }} />
                ) : (
                    <Text
                        level={3}
                        variant="contrast"
                        sx={{
                            fontWeight: 700,
                            paddingInline: 6,
                        }}
                    >
                        {copy.setUpProduct}
                    </Text>
                )}

                {/* Right: Close button (hidden in Job Search app) */}
                <Button
                    variant="tertiary"
                    onClick={handleClose}
                    aria-label={gt.gettext('Close')}
                    sx={{
                        ...(isProBrandingEnabled ? jsProThemeButton.tertiary : {}),
                        // Use visibility to keep the layout consistent in Job Search app
                        visibility: isJobSearchApp ? 'hidden' : undefined,
                    }}
                >
                    <Close size="md" aria-hidden />
                </Button>
            </Flex>

            {/* Progress Bar - Hidden on the complete page and in the
                progressive-automation lightweight variant */}
            {!isCompletePage && !isProgressiveAutomation && (
                <ProgressBar
                    aria-label={gt.gettext('Setup progress')}
                    sx={isProBrandingEnabled ? jsProThemeProgressBar : {}}
                    value={calculateProgress()}
                />
            )}

            {/* Exit Dialog */}
            <ExitDialog
                isOpen={isExitDialogOpen}
                onExitClick={handleExitClick}
                onCancelClick={handleCancelClick}
            />
        </Flex>
    );
};

export default AutoApplySetupNavigation;
